// src/components/Login.js
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase/firebase-config";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { setClientSession } from "../utils/sessionSecurity";
import backgroundImage from "../assests/myimg.jpg"; // your image

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );
      const user = userCredential.user;

      // Unique session id per device
      const sessionId = uuidv4();

      // Persist the client session in one shared helper.
      setClientSession({
        userEmail: email.trim(),
        userId: user.uid,
        sessionId,
      });

      // Overwrite session in Firestore -> logs out other devices
      await setDoc(doc(db, "sessions", user.uid), {
        activeSessionId: sessionId,
        userId: user.uid,
        userEmail: email.trim(),
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      });

      navigate(location.state?.returnTo === "/upgrade-line" ? "/upgrade-line" : "/dashboard/all-details", { replace: true });
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (code) => {
    switch (code) {
      case "auth/user-not-found":
        return "No user found with this email.";
      case "auth/wrong-password":
        return "Incorrect password.";
      case "auth/invalid-email":
        return "Invalid email address.";
      case "auth/too-many-requests":
        return "Too many failed attempts. Please try again later.";
      default:
        return "Failed to log in. Please try again.";
    }
  };

  return (
    <div className="login-page">
      <div className="bg-photo">
        <div className="bg-overlay" />
      </div>

      <div className="overlay">
        <div className="yogi-title">
          <h1>YOGI</h1>
          <p>Welcome back, let’s continue the journey 🚀</p>
        </div>

        <div className="login-form">
          <h2 className="mb-4">Login</h2>
          {error && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group mb-3">
              <label>Password</label>
              <input
                type="password"
                className="form-control"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        .login-page {
          position: relative;
          min-height: 100vh;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .bg-photo {
          position: absolute;
          inset: -2%;
          background: url(${backgroundImage}) no-repeat center/cover;
          filter: brightness(1.05) contrast(1.05);
          animation: panZoom 28s ease-in-out infinite;
        }
        .bg-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, #22d3ee, #a78bfa, #f472b6, #fbbf24, #22d3ee);
          background-size: 300% 300%;
          mix-blend-mode: overlay;
          animation: gradientShift 6s ease infinite;
          opacity: 0.6;
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes panZoom {
          0%   { transform: scale(1.05) translate(0,0); }
          50%  { transform: scale(1.02) translate(0.6%,-0.6%); }
          100% { transform: scale(1.05) translate(0,0); }
        }

        .overlay {
          position: relative;
          z-index: 2;
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding: 2rem 5%;
        }

        .yogi-title h1 {
          font-size: 5rem;
          font-weight: 900;
          background: linear-gradient(90deg, #22d3ee, #a78bfa, #f472b6, #fbbf24);
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          animation: shine 6s linear infinite;
        }
        .yogi-title p {
          color: #fff;
          font-size: 1.2rem;
          margin-top: 0.5rem;
        }
        @keyframes shine {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }

        .login-form {
          background: rgba(255,255,255,0.25);
          backdrop-filter: blur(12px);
          padding: 30px;
          border-radius: 16px;
          width: 100%;
          max-width: 360px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.25);
        }
        .login-form h2 {
          text-align: center;
          font-weight: bold;
          color: #fff;
        }
        label {
          font-weight: 600;
          color: #fff;
        }

        @media(max-width: 992px) {
          .overlay {
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .yogi-title {
            order: 1;
            text-align: center;
          }
          .login-form {
            order: 2;
            margin-top: auto;
            width: 90%;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
